/**
 * TBD/NTB-rapport (M8) — workspace-breed overzicht van alle required-velden
 * die nog een TBD/NTB-placeholder dragen, zodat modelleurs zien wat er nog
 * ingevuld moet worden.
 *
 * De sentinel (`TBD` / `NTB`, case-insensitive) mag in een required veld staan
 * zonder de validatie te breken (M5-§6, `isTbdSentinel`). Dit rapport scant de
 * store (actieve branch) op zulke waarden — plain string, bilingual `{nl,en}`
 * én array-elementen — en resolvet het veld-label + de bron (stereotype/type)
 * die het declareert.
 *
 * Rijen zijn gegroepeerd per stereotype/type dat het veld declareert en
 * binnen een groep gesorteerd op laatst-gewijzigd (nieuwste eerst). Klik op
 * een object springt naar dat concept/relatie in het AttributePanel (sluit de
 * modal) zodat je 't meteen kunt invullen.
 */
import { type AttributeSchema, isTbdSentinel } from "@ae/domain";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../registry";
import { useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";
import { ld, pill } from "./ledgerUi";

interface TbdEntry {
  kind: "concept" | "relation";
  id: string;
  objectName: string;
  typeLabel: string;
  ownerLabel: string;
  fieldLabel: string;
  value: string;
  updatedAt: string;
}

/** Eerste TBD/NTB-sentinel in een waarde (plain / bilingual / array), of "".
 *  Taalbewust: bij een bilinguale `{nl,en}`-bag krijgt de sentinel in de
 *  UI-taal voorrang (EN → "TBD", NL → "NTB") zodat 't rapport de placeholder
 *  in de gelezen taal toont i.p.v. altijd de eerste key. */
function sentinelValue(v: unknown, lang: string): string {
  if (isTbdSentinel(v)) return String(v).trim().toUpperCase();
  if (Array.isArray(v)) {
    for (const el of v) {
      const hit = sentinelValue(el, lang);
      if (hit) return hit;
    }
    return "";
  }
  if (v && typeof v === "object") {
    const obj = v as Record<string, unknown>;
    if (isTbdSentinel(obj[lang])) return String(obj[lang]).trim().toUpperCase();
    for (const el of Object.values(obj)) {
      const hit = sentinelValue(el, lang);
      if (hit) return hit;
    }
  }
  return "";
}

/** key → { fieldLabel, ownerLabel } voor de attributen van type + stereotypes.
 *  Stereotype-declaraties winnen van base-type bij dezelfde key. */
function buildFieldIndex(
  attrsFromType: AttributeSchema[],
  typeLabel: string,
  stereotypes: string[],
  lang: string,
): Map<string, { fieldLabel: string; ownerLabel: string }> {
  const reg = getRegistry();
  const index = new Map<string, { fieldLabel: string; ownerLabel: string }>();
  for (const a of attrsFromType) {
    index.set(a.name, { fieldLabel: resolveBilingual(a.label, lang), ownerLabel: typeLabel });
  }
  for (const stId of stereotypes) {
    const st = reg.getStereotype(stId);
    if (!st) continue;
    const ownerLabel = resolveBilingual(st.label, lang);
    for (const a of reg.resolveStereotypeAttributes(st)) {
      index.set(a.name, { fieldLabel: resolveBilingual(a.label, lang), ownerLabel });
    }
  }
  return index;
}

export function ValidationSettings({ onClose }: { onClose?: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const revision = useWorkspaceData((s) => s.revision);
  const selectConcept = useUi((s) => s.selectConcept);
  const selectRelation = useUi((s) => s.selectRelation);

  const jump = (e: TbdEntry) => {
    if (e.kind === "concept") selectConcept(e.id);
    else selectRelation(e.id);
    onClose?.();
  };

  const entries = useMemo<TbdEntry[]>(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const out: TbdEntry[] = [];

    for (const c of store.listConcepts()) {
      if (c.tombstoneInBranch) continue;
      const et = reg.getElementType(c.typeId);
      const typeLabel = et ? resolveBilingual(et.label, lang) : c.typeId;
      const index = buildFieldIndex(et?.attributes ?? [], typeLabel, c.stereotypes, lang);
      for (const [key, value] of Object.entries(c.attributes)) {
        const sentinel = sentinelValue(value, lang);
        if (!sentinel) continue;
        const meta = index.get(key);
        out.push({
          kind: "concept",
          id: c.id,
          objectName: resolveBilingual(c.name, lang) || c.id,
          typeLabel,
          ownerLabel: meta?.ownerLabel ?? typeLabel,
          fieldLabel: meta?.fieldLabel ?? key,
          value: sentinel,
          updatedAt: c.updatedAt ?? "",
        });
      }
    }

    for (const r of store.listRelations()) {
      if (r.tombstoneInBranch) continue;
      const rt = reg.getRelationType(r.typeId);
      const typeLabel = rt ? resolveBilingual(rt.label, lang) : r.typeId;
      const index = buildFieldIndex(rt?.attributes ?? [], typeLabel, r.stereotypes, lang);
      const src = store.getConcept(r.sourceConceptId);
      const tgt = store.getConcept(r.targetConceptId);
      const fallbackName = `${src ? resolveBilingual(src.name, lang) : "?"} → ${
        tgt ? resolveBilingual(tgt.name, lang) : "?"
      }`;
      for (const [key, value] of Object.entries(r.attributes)) {
        const sentinel = sentinelValue(value, lang);
        if (!sentinel) continue;
        const meta = index.get(key);
        out.push({
          kind: "relation",
          id: r.id,
          objectName: resolveBilingual(r.label, lang) || fallbackName,
          typeLabel,
          ownerLabel: meta?.ownerLabel ?? typeLabel,
          fieldLabel: meta?.fieldLabel ?? key,
          value: sentinel,
          updatedAt: r.updatedAt ?? "",
        });
      }
    }

    // Laatst gewijzigd eerst; binnen een groep blijft die volgorde behouden.
    return out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [revision, lang]);

  // Groepeer per bron-stereotype/type (het label dat het veld declareert),
  // secties alfabetisch; rijen binnen een sectie erven de updatedAt-desc-orde.
  const groups = useMemo(() => {
    const map = new Map<string, TbdEntry[]>();
    for (const e of entries) {
      const arr = map.get(e.ownerLabel) ?? [];
      arr.push(e);
      map.set(e.ownerLabel, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], lang));
  }, [entries, lang]);

  const objectCount = useMemo(
    () => new Set(entries.map((e) => `${e.kind}:${e.id}`)).size,
    [entries],
  );

  return (
    <div className="flex flex-col gap-4">
      <p className={ld.viewIntro}>{t("settings.validation.description")}</p>
      {entries.length === 0 ? (
        <p className={`${ld.well} text-(--ld-muted) text-sm`}>{t("settings.validation.empty")}</p>
      ) : (
        <>
          <p className="font-medium text-sm">
            {t("settings.validation.summary", { fields: entries.length, objects: objectCount })}
          </p>
          {/* Eén tabel met `table-fixed` + vaste kolombreedtes zodat de kolommen
              over álle groepen uitlijnen; elke groep is een koprij + z'n rijen. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-[44%]" />
                <col className="w-[24%]" />
                <col className="w-[12%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead>
                <tr className="text-left text-(--ld-muted) text-xs">
                  <th className="px-2 pb-2 font-normal">{t("settings.validation.col.object")}</th>
                  <th className="px-2 pb-2 font-normal">{t("settings.validation.col.field")}</th>
                  <th className="px-2 pb-2 font-normal">{t("settings.validation.col.value")}</th>
                  <th className="px-2 pb-2 text-right font-normal">
                    {t("settings.validation.col.lastEdit")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.map(([owner, rows]) => (
                  <Fragment key={owner}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className="border-(--ld-border) border-b px-2 pt-5 pb-1.5 text-left font-semibold text-[13px]"
                      >
                        {owner}{" "}
                        <span className="font-normal text-(--ld-muted) text-xs tabular-nums">
                          ({rows.length})
                        </span>
                      </th>
                    </tr>
                    {rows.map((e) => (
                      <tr key={`${e.id}:${e.fieldLabel}`} className="border-(--ld-rule) border-b">
                        <td className="px-2 py-1.5 align-top">
                          <button
                            type="button"
                            className="break-words rounded-sm text-left underline-offset-2 hover:text-(--ld-accent) hover:underline"
                            onClick={() => jump(e)}
                            title={t("settings.validation.jumpHint")}
                          >
                            {e.objectName}
                            <span className="ml-1.5 text-(--ld-muted) text-xs">{e.typeLabel}</span>
                          </button>
                        </td>
                        <td className="break-words px-2 py-1.5 align-top text-(--ld-muted)">
                          {e.fieldLabel}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className={pill.notice}>{e.value}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right align-top text-(--ld-muted) tabular-nums">
                          {e.updatedAt ? e.updatedAt.slice(0, 16) : "—"}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
